import { useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "../../lib/api";
import { StoreLayout } from "../../components/StoreLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { CheckCircle, XCircle, Sparkle, SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function SellOffer() {
  const { bookingId } = useParams();
  const [phone, setPhone] = useState("");
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpToken, setOtpToken] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [emailMasked, setEmailMasked] = useState("");
  const [result, setResult] = useState(null);

  const lookup = async () => {
    if (!/^\d{6,}$/.test(phone)) { toast.error("Enter your registered mobile number"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sell/offer/lookup", { booking_id: bookingId, phone });
      setOrder(data); setEmailMasked(data.email_masked || "");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const sendOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sell/offer/${bookingId}/otp/send`, { phone });
      setOtpToken(data.token); setEmailMasked(data.email_masked || emailMasked); setOtpSent(true);
      toast.success("OTP sent to your email");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const verifyAndRespond = async (decision) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sell/offer/${bookingId}/respond`, { decision, otp_token: otpToken, code: otpCode });
      setResult(data); toast.success(decision === "accept" ? "Offer accepted" : "Offer declined");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const co = order?.counter_offer;

  return (
    <StoreLayout>
      <div className="max-w-lg mx-auto px-4 py-10" data-testid="sell-offer-page">
        <h1 className="font-[Outfit] text-3xl font-extrabold tracking-tight">Your revised offer</h1>
        <p className="text-[#475569] mt-2">Booking ID <b>{bookingId}</b></p>

        {!order && (
          <div className="mt-6 space-y-3">
            <Label className="text-sm font-semibold">Verify it's you — enter your registered mobile</Label>
            <Input placeholder="Mobile number" className="rounded-xl" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} data-testid="offer-phone" />
            <Button onClick={lookup} disabled={busy} className="rounded-xl w-full h-12 bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="offer-lookup">{busy ? "Checking…" : "View Offer"}</Button>
          </div>
        )}

        {order && !result && (
          <div className="mt-6" data-testid="offer-detail">
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-5 text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-[#475569]">Device</span><span className="font-semibold">{order.category_name} {order.brand_name} {order.model_name}</span></div>
              <div className="flex justify-between"><span className="text-[#475569]">Initial estimate</span><span className="font-semibold">{fmt(order.estimated_price)}</span></div>
            </div>

            {co && co.status === "pending" && (
              <div className="mt-6 text-center border-2 border-[#4F46E5]/20 rounded-2xl p-6 bg-[#4F46E5]/5">
                <Sparkle size={30} weight="fill" className="text-[#4F46E5] mx-auto" />
                <p className="text-[#475569] mt-2">Our revised offer after inspection</p>
                <div className="font-[Outfit] text-5xl font-extrabold text-[#1E293B] mt-1" data-testid="offer-amount">{fmt(co.amount)}</div>
                {co.reason && <p className="text-sm text-[#475569] mt-3">{co.reason}</p>}

                {!otpSent ? (
                  <Button onClick={sendOtp} disabled={busy} className="rounded-xl w-full mt-6 h-12 bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="offer-otp-send">
                    {busy ? "Sending…" : "Verify to Respond"}
                  </Button>
                ) : (
                  <div className="mt-5 space-y-3 text-left">
                    <p className="text-xs text-[#475569]">Enter the 6-digit OTP sent to {emailMasked}</p>
                    <Input placeholder="6-digit OTP" maxLength={6} className="rounded-xl text-center tracking-[0.4em] text-lg" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))} data-testid="offer-otp-input" />
                    <div className="grid grid-cols-2 gap-3">
                      <Button onClick={() => verifyAndRespond("reject")} disabled={busy || otpCode.length !== 6} variant="outline" className="rounded-xl h-12 border-red-300 text-red-600 hover:bg-red-50" data-testid="offer-reject">Decline</Button>
                      <Button onClick={() => verifyAndRespond("accept")} disabled={busy || otpCode.length !== 6} className="rounded-xl h-12 bg-[#166534] hover:bg-[#14532d]" data-testid="offer-accept">Accept</Button>
                    </div>
                    <button onClick={sendOtp} className="text-sm text-[#4F46E5] font-semibold w-full">Resend OTP</button>
                  </div>
                )}
              </div>
            )}

            {co && co.status !== "pending" && (
              <div className="mt-6 text-center border rounded-2xl p-6" data-testid="offer-already">
                {co.status === "accepted"
                  ? <><CheckCircle size={40} weight="fill" className="text-[#166534] mx-auto" /><p className="font-semibold mt-2">You've accepted this offer of {fmt(co.amount)}.</p></>
                  : <><XCircle size={40} weight="fill" className="text-red-500 mx-auto" /><p className="font-semibold mt-2">This offer was declined.</p></>}
              </div>
            )}

            {!co && (
              <p className="mt-6 text-center text-[#475569] text-sm">No revised offer has been shared yet. We'll email you once inspection is complete.</p>
            )}
          </div>
        )}

        {result && (
          <div className="mt-8 text-center" data-testid="offer-result">
            {result.status === "price_accepted"
              ? <><CheckCircle size={56} weight="fill" className="text-[#166534] mx-auto" /><h2 className="font-[Outfit] text-2xl font-extrabold mt-3">Offer accepted!</h2><p className="text-[#475569] mt-2">We'll proceed with payment of {fmt(result.counter_offer?.amount)} as per the confirmed process.</p></>
              : <><XCircle size={56} weight="fill" className="text-red-500 mx-auto" /><h2 className="font-[Outfit] text-2xl font-extrabold mt-3">Offer declined</h2><p className="text-[#475569] mt-2">Your request has been cancelled. Feel free to start again anytime.</p></>}
          </div>
        )}
      </div>
    </StoreLayout>
  );
}
